import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { setupInterceptors } from './services/api';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Layout
import MainLayout from './layouts/MainLayout';

// Components
import PrivateRoute from './components/PrivateRoute';

// Lazy loaded pages
const Login = React.lazy(() => import('./pages/Login'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));

// Trip Pages
const TripList = React.lazy(() => import('./pages/TripList'));
const TripCreate = React.lazy(() => import('./pages/TripCreate'));
const TripDetail = React.lazy(() => import('./pages/TripDetail'));
const TripEdit = React.lazy(() => import('./pages/TripEdit'));

// Vehicle Pages
const VehicleList = React.lazy(() => import('./pages/VehicleList'));
const VehicleCreate = React.lazy(() => import('./pages/VehicleCreate'));
const VehicleDetail = React.lazy(() => import('./pages/VehicleDetail'));

// Driver Pages
const DriverList = React.lazy(() => import('./pages/DriverList'));
const DriverCreate = React.lazy(() => import('./pages/DriverCreate'));
const DriverDetail = React.lazy(() => import('./pages/DriverDetail'));
const DriverEdit = React.lazy(() => import('./pages/DriverEdit'));

// Fuel Pages
const FuelList = React.lazy(() => import('./pages/FuelList'));
const FuelCreate = React.lazy(() => import('./pages/FuelCreate'));
const FuelDetail = React.lazy(() => import('./pages/FuelDetail'));
const FuelEdit = React.lazy(() => import('./pages/FuelEdit'));

// Maintenance Pages
const MaintenanceList = React.lazy(() => import('./pages/MaintenanceList'));
const MaintenanceCreate = React.lazy(() => import('./pages/MaintenanceCreate'));
const MaintenanceDetail = React.lazy(() => import('./pages/MaintenanceDetail'));
const MaintenanceEdit = React.lazy(() => import('./pages/MaintenanceEdit'));

// Report Pages
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));

// Kullanıcı Yönetimi (sadece genel admin)
const UserManagement = React.lazy(() => import('./pages/UserManagement'));

// Loading component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// Setup interceptors
setupInterceptors(store);

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            
            <Route path="/" element={<PrivateRoute><MainLayout /></PrivateRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
            
            {/* Yol Kayıtları */}
            <Route path="trips">
              <Route index element={<TripList />} />
              <Route path="create" element={<TripCreate />} />
              <Route path=":id" element={<TripDetail />} />
              <Route path=":id/edit" element={<TripEdit />} />
            </Route>

            {/* Araçlar */}
            <Route path="vehicles">
              <Route index element={<VehicleList />} />
              <Route path="create" element={<VehicleCreate />} />
              <Route path=":id" element={<VehicleDetail />} />
            </Route>

            {/* Sürücüler */}
            <Route path="drivers">
              <Route index element={<DriverList />} />
              <Route path="create" element={<DriverCreate />} />
              <Route path=":id" element={<DriverDetail />} />
              <Route path=":id/edit" element={<DriverEdit />} /> {/* Add edit route */}
            </Route>

            {/* Yakıt Kayıtları */}
            <Route path="fuel">
              <Route index element={<FuelList />} />
              <Route path="create" element={<FuelCreate />} />
              <Route path=":id" element={<FuelDetail />} />
              <Route path=":id/edit" element={<FuelEdit />} />
            </Route>

            {/* Bakım Kayıtları */}
            <Route path="maintenance">
              <Route index element={<MaintenanceList />} />
              <Route path="create" element={<MaintenanceCreate />} />
              <Route path=":id" element={<MaintenanceDetail />} />
              <Route path=":id/edit" element={<MaintenanceEdit />} />
            </Route>

            {/* Raporlar */}
            <Route path="reports" element={<ReportsPage />} />

            {/* Kullanıcı Yönetimi */}
            <Route path="users" element={<UserManagement />} />

            {/* Mesajlar */}
            {/* <Route path="messages">
              <Route index element={<MessageList />} />
              <Route path="create" element={<MessageCreate />} />
            </Route> */}
            </Route>
          </Routes>
        </Suspense>
      </Router>
      <ToastContainer position="top-right" autoClose={3000} />
    </Provider>
  );
};

export default App;
